import React, { useEffect, useMemo, useState } from "react";
import { GetUserPaymentSummary } from "../API/RequestsServices";

const COLORS = {
  Paid: "#10B981",            // green
  PendingApproval: "#0EA5E9", // amber #F59E0B
  Pending: "#F59E0B",         // blue
  Expired: "#F43F5E",         // red
  Empty: "#E5E7EB",           // gray
};

function buildConicGradient(segments) {
  const total = segments.reduce((s, a) => s + a.value, 0);
  if (!total) return `conic-gradient(${COLORS.Empty} 0 360deg)`;
  let acc = 0;
  const stops = segments.map((s) => {
    const slice = (s.value / total) * 360;
    const start = acc;
    const end = acc + slice;
    acc = end;
    return `${s.color} ${start}deg ${end}deg`;
  });
  return `conic-gradient(${stops.join(", ")})`;
}

function DonutMini({ title, bucket }) {
  const segs = useMemo(() => {
    if (!bucket) return [];
    const paid = bucket.paid ?? bucket.Paid ?? 0;
    const pa   = bucket.pendingApproval ?? bucket.PendingApproval ?? 0;
    const pend = bucket.pending ?? bucket.Pending ?? 0;
    const exp  = bucket.expired ?? bucket.Expired ?? 0;
    return [
      { label: "Paid", value: paid, color: COLORS.Paid },
      { label: "Pending Approval", value: pa, color: COLORS.PendingApproval },
      { label: "Pending", value: pend, color: COLORS.Pending },
      { label: "Expired", value: exp, color: COLORS.Expired },
    ];
  }, [bucket]);

  const total = segs.reduce((s, i) => s + i.value, 0);
  const bg = buildConicGradient(segs);

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 text-sm font-semibold text-gray-900">{title}</div>
      <div className="flex items-center gap-4">
        {/* Donut */}
        <div className="relative h-32 w-32">
          <div className="h-full w-full rounded-full" style={{ background: bg }} />
          <div className="absolute inset-4 rounded-full bg-white" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-xl font-semibold text-gray-900">{total}</div>
              <div className="text-[10px] text-gray-500">Total</div>
            </div>
          </div>
        </div>
        {/* Legend */}
        <div className="grid grid-cols-1 gap-1 text-xs text-gray-700">
          {segs.map((s) => {
            const pct = total ? Math.round((s.value / total) * 100) : 0;
            return (
              <div key={s.label} className="flex items-center gap-2">
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: s.color }}
                />
                <span className="truncate">
                  <span className="font-medium">{s.label}</span>{" "}
                  <span className="text-gray-500">{s.value} ({pct}%)</span>
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function DonutBoard({ toast }) {
  const token = sessionStorage.getItem("token");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    await GetUserPaymentSummary(token, toast, setReport);
    setLoading(false);
  };

  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const buckets = useMemo(
    () => ({
      today: report?.today ?? report?.Today,
      week:  report?.week  ?? report?.Week,
      month: report?.month ?? report?.Month,
      year:  report?.year  ?? report?.Year,
    }),
    [report]
  );

  return (
    <div className="mt-6">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-base font-semibold text-gray-900">
          Payment Status Overview
        </h3>
        <button
          onClick={load}
          className="px-3 py-1.5 text-sm rounded-md border border-gray-300 hover:bg-gray-50"
        >
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center p-10">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
        </div>
      ) : !report ? (
        <div className="text-sm text-gray-500">No data.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <DonutMini title="Today" bucket={buckets.today} />
          <DonutMini title="This Week" bucket={buckets.week} />
          <DonutMini title="This Month" bucket={buckets.month} />
          <DonutMini title="This Year" bucket={buckets.year} />
        </div>
      )}
    </div>
  );
}
